/**
 * Filter Dropdown Component
 * 
 * Dropdown filter with Status, Board (Category), and Created Date options
 */
import { useState, useRef, useEffect } from '@wordpress/element';
import { __ } from '@wordpress/i18n';

const FilterDropdown = ({ 
    statuses = {},
    categories = {},
    activeFilters = {},
    onFilterChange 
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [expandedFilter, setExpandedFilter] = useState(null);
    const dropdownRef = useRef(null);

    // Build status options from dynamic prop.
    const statusOptions = {};
    Object.keys(statuses).forEach((slug) => {
        statusOptions[slug] = statuses[slug].label || slug;
    });

    const defaultCategories = {
        'feature_requests': __( 'Feature Requests', 'changeloger' ),
        'bug_reports': __( 'Bug Reports', 'changeloger' ),
        'integrations': __( 'Integrations', 'changeloger' ),
        'languages': __( 'Languages', 'changeloger' )
    };

    const dateOptions = {
        'today': __( 'Today', 'changeloger' ),
        'this_week': __( 'This Week', 'changeloger' ),
        'this_month': __( 'This Month', 'changeloger' ),
        'older': __( 'Older', 'changeloger' )
    };

    const categoryOptions = Object.keys(categories).length > 0 ? categories : defaultCategories;

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setIsOpen(false);
                setExpandedFilter(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleFilterSelect = (filterType, value) => {
        onFilterChange(filterType, value);
        setExpandedFilter(null);
    };

    const clearFilter = (filterType) => {
        onFilterChange(filterType, null);
    };

    const getActiveFilterCount = () => {
        return Object.values(activeFilters).filter(v => v).length;
    };

    const filterCount = getActiveFilterCount();

    return (
        <div className="cha-filter-dropdown" ref={dropdownRef}>
            {/* Filter Button */}
            <button 
                className={`cha-filter-btn ${isOpen ? 'active' : ''} ${filterCount > 0 ? 'has-filters' : ''}`}
                onClick={() => setIsOpen(!isOpen)}
            >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="18" height="18">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                {filterCount > 0 && (
                    <span className="cha-filter-count">{filterCount}</span>
                )}
            </button>

            {/* Dropdown Menu */}
            {isOpen && (
                <div className="cha-filter-menu">
                    <div className="cha-filter-search">
                        <input 
                            type="text" 
                            placeholder={__( 'Search filter...', 'changeloger' )} 
                            className="cha-filter-search-input"
                        />
                    </div>

                    <div className="cha-filter-options">
                        {/* Status Filter */}
                        <div className="cha-filter-option">
                            <button 
                                className={`cha-filter-option-btn ${expandedFilter === 'status' ? 'expanded' : ''}`}
                                onClick={() => setExpandedFilter(expandedFilter === 'status' ? null : 'status')}
                            >
                                <span className="cha-filter-icon">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="16" height="16">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </span>
                                {__( 'Status', 'changeloger' )}
                                {activeFilters.status && (
                                    <span className="cha-filter-active-value">{statusOptions[activeFilters.status]}</span>
                                )}
                            </button>
                            {expandedFilter === 'status' && (
                                <div className="cha-filter-values">
                                    {Object.entries(statusOptions).map(([key, label]) => (
                                        <button 
                                            key={key}
                                            className={`cha-filter-value ${activeFilters.status === key ? 'selected' : ''}`}
                                            onClick={() => handleFilterSelect('status', key)}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Board/Category Filter */}
                        <div className="cha-filter-option">
                            <button 
                                className={`cha-filter-option-btn ${expandedFilter === 'board' ? 'expanded' : ''}`}
                                onClick={() => setExpandedFilter(expandedFilter === 'board' ? null : 'board')}
                            >
                                <span className="cha-filter-icon">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="16" height="16">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                    </svg>
                                </span>
                                {__( 'Board', 'changeloger' )}
                                {activeFilters.board && (
                                    <span className="cha-filter-active-value">{categoryOptions[activeFilters.board]}</span>
                                )}
                            </button>
                            {expandedFilter === 'board' && (
                                <div className="cha-filter-values">
                                    {Object.entries(categoryOptions).map(([key, label]) => (
                                        <button 
                                            key={key}
                                            className={`cha-filter-value ${activeFilters.board === key ? 'selected' : ''}`}
                                            onClick={() => handleFilterSelect('board', key)}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Created Date Filter */}
                        <div className="cha-filter-option">
                            <button 
                                className={`cha-filter-option-btn ${expandedFilter === 'date' ? 'expanded' : ''}`}
                                onClick={() => setExpandedFilter(expandedFilter === 'date' ? null : 'date')}
                            >
                                <span className="cha-filter-icon">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="16" height="16">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                </span>
                                {__( 'Created date', 'changeloger' )}
                                {activeFilters.date && (
                                    <span className="cha-filter-active-value">{dateOptions[activeFilters.date]}</span>
                                )}
                            </button>
                            {expandedFilter === 'date' && (
                                <div className="cha-filter-values">
                                    {Object.entries(dateOptions).map(([key, label]) => (
                                        <button 
                                            key={key}
                                            className={`cha-filter-value ${activeFilters.date === key ? 'selected' : ''}`}
                                            onClick={() => handleFilterSelect('date', key)}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Clear All Filters */}
                    {filterCount > 0 && (
                        <div className="cha-filter-clear">
                            <button 
                                className="cha-filter-clear-btn"
                                onClick={() => onFilterChange('clear', null)}
                            >
                                {__( 'Clear all filters', 'changeloger' )}
                            </button>
                        </div>
                    )}
                </div>
            )}

        </div>
    );
};

export default FilterDropdown;
